import React, { useCallback, useEffect, useRef } from "react";
import * as DateFns from "date-fns";
import * as D3 from "d3";

const fixDecimals = (number, decimals) => {
  if (Number.isNaN(+number)) return "-";
  const splitedNumber = (+parseFloat(number).toFixed(decimals))
    .toString()
    .split(".");
  let newNumber = (+parseFloat(number).toFixed(decimals)).toString();
  let decimalSize = 0;
  if (splitedNumber.length === 1) {
    if (decimals > 0) newNumber += ".";
  } else {
    decimalSize = splitedNumber[1].length;
  }
  for (let findex = 0; findex < decimals - decimalSize; findex++)
    newNumber += "0";
  return newNumber;
};

export const VelocityGraph = ({
  data,
  width,
  height,
  periods,
  dataPlays = [],
}) => {
  const d3Container = useRef(null);
  const refData = useRef({ x: 0, diffScroll: 0 });
  // Keep scroll listener ref so we can remove it on cleanup
  const scrollListenerRef = useRef(null);

  const printTime = useCallback((timeStamp) => {
    const date = new Date(timeStamp);
    return DateFns.format(date, "hh:mm:ss");
  }, []);

  useEffect(() => {
    const container = D3.select(d3Container.current);
    container.selectAll("*").remove();

    // ✅ Clean up previous scroll listener before rebuilding
    if (scrollListenerRef.current) {
      const prevScroller = document.getElementById("velocity-scroll");
      if (prevScroller) {
        prevScroller.removeEventListener("scroll", scrollListenerRef.current);
      }
      scrollListenerRef.current = null;
    }

    const orderedData = (data || []).sort(
      (a, b) => +Object.keys(a)[0] - +Object.keys(b)[0]
    );
    const xMax = D3.max(orderedData, (d) => +Object.keys(d)[0]);
    data.push(
      ...periods
        .filter((period) => period.endTs && period.endTs > xMax)
        .map((period) => ({ [period.endTs]: 0 }))
    );

    const graphHeight = height || 100;
    const graphWidth = orderedData.length * 5;

    container
      .append("h6")
      .text("VELOCITY GRAPH (MPH)")
      .attr("style", "position: fixed; left: 14px; width: 770px; z-index: -1;");

    const marginTop = 30;
    const marginBottom = 30;
    const toolTipSvg = container
      .append("svg")
      .attr("width", graphWidth)
      .attr("height", marginTop)
      .attr(
        "style",
        "overflow: visible; margin-top: 15px; left: 15px; position: absolute; z-index: 1"
      )
      .attr("viewBox", `0 0 ${graphWidth} ${marginTop}`);

    const divWidth = width || Math.max(orderedData.length * 5, 200);
    const div = container
      .append("div")
      .attr(
        "style",
        `width: ${divWidth}px; height: ${
          graphHeight + 15 + 7
        }px; overflow-x: auto; position: absolute;`
      )
      .attr("class", "velocity-graph")
      .attr("id", "velocity-scroll");
    // ✅ No D3 scroll listener here anymore

    const svg = div.append("svg");
    svg
      .attr("width", graphWidth)
      .attr("height", graphHeight)
      .attr("style", "overflow: visible; margin-top: 15px;")
      .attr("viewBox", `0 0 ${graphWidth} ${graphHeight}`);

    if (!orderedData || orderedData.length < 1) {
      svg
        .append("text")
        .text("No data available")
        .attr("x", graphWidth / 2)
        .attr("y", graphHeight / 2 + marginTop)
        .attr("text-anchor", "middle")
        .attr("alignment-baseline", "central");
      return;
    }

    const heightBoxGraph = graphHeight - marginTop - marginBottom;
    const maxX = D3.max(orderedData, (d) => +Object.keys(d)[0]);
    const minX = D3.min(orderedData, (d) => +Object.keys(d)[0]);

    const scaleX = D3.scaleLinear().domain([minX, maxX]).range([0, graphWidth]);
    const topPadding = 20;
    const maxY = D3.max(orderedData, (d) => +d[Object.keys(d)[0]]);
    const scaleY = D3.scaleLinear()
      .domain([0, maxY])
      .range([heightBoxGraph + marginTop, marginTop + topPadding]);

    const mainGraphic = svg.append("g");

    mainGraphic
      .append("rect")
      .style("fill", "#EFF6FA")
      .attr("width", graphWidth)
      .attr("height", heightBoxGraph)
      .attr("y", marginTop);

    mainGraphic
      .append("path")
      .datum(orderedData)
      .attr(
        "d",
        D3.area()
          .x((d) => scaleX(Object.keys(d)[0] * 1))
          .y0(scaleY(0))
          .y1((d) => scaleY(d[Object.keys(d)[0]]))
      )
      .style("stroke", "#007ABA")
      .style("fill", "#E0F2F7")
      .attr("stroke-linecap", "round");

    const periodsGraph = mainGraphic.append("g");
    (periods || [])
      .sort((a, b) => b.endTs - a.endTs)
      .forEach((period) => {
        const periodGraph = periodsGraph.append("g");
        const { endTs = new Date(), drillType } = period;
        periodGraph
          .append("line")
          .style("stroke", "#333D47")
          .attr("x1", scaleX(endTs) - 1)
          .attr("y1", marginTop)
          .attr("x2", scaleX(endTs) - 1)
          .attr("y2", marginTop + heightBoxGraph + marginBottom);
        periodGraph
          .append("text")
          .text(drillType)
          .attr("font-size", "14")
          .attr("fill", "#333D47")
          .attr("text-anchor", "end")
          .attr("style", "font-family: 'Roboto'; font-weight: 500;")
          .attr("x", scaleX(endTs) - 5)
          .attr("y", marginTop + heightBoxGraph + 15);
        periodGraph
          .append("text")
          .text(printTime(endTs))
          .attr("font-size", "14")
          .attr("fill", "#94A0AA")
          .attr("text-anchor", "end")
          .attr("style", "font-family: 'Roboto'; font-weight: 400;")
          .attr("x", scaleX(endTs) - 5)
          .attr("y", marginTop + heightBoxGraph + 30);
      });

    svg
      .append("rect")
      .style("fill", "none")
      .style("pointer-events", "all")
      .attr("width", graphWidth)
      .attr("height", heightBoxGraph)
      .attr("y", marginTop)
      .on("mouseover", mouseover)
      .on("mousemove", function (event) {
        const x = scaleX.invert(D3.pointer(event)[0]);
        refData.current.x = x;
        mousemove(x);
      });

    /************************ PLAYS ***********************/
    const playsGraph = svg.append("g");
    (dataPlays || [])
      .sort((a, b) => b.timeStamp - a.timeStamp)
      .forEach((play) => {
        const playGraph = playsGraph;
        const box = playGraph
          .append("rect")
          .attr("fill", "white")
          .attr("rx", 4)
          .attr("ry", 4)
          .style("opacity", 0);
        const textLabel = playGraph.append("text");
        const textTime = playGraph.append("text");
        const strokeWidth = 2;
        const size = 6;
        const line = playGraph
          .append("line")
          .style("stroke", "#007ABA")
          .style("stroke-width", strokeWidth)
          .style("stroke-dasharray", "5 2")
          .style("opacity", "0.6")
          .attr("x1", scaleX(play.timeStamp))
          .attr("y1", marginTop)
          .attr("x2", scaleX(play.timeStamp))
          .attr("y2", marginTop + heightBoxGraph + marginBottom / 2 - size);
        playGraph
          .append("line")
          .style("stroke-width", strokeWidth * 3)
          .attr("x1", scaleX(play.timeStamp))
          .attr("y1", marginTop)
          .attr("x2", scaleX(play.timeStamp))
          .attr("y2", marginTop + heightBoxGraph + marginBottom);
        const xx = scaleX(play.timeStamp);
        const yy = marginTop + heightBoxGraph + marginBottom / 2 - size;
        const polygon = playGraph
          .append("polygon")
          .attr("fill", "#007ABA")
          .attr("stroke", "#007ABA")
          .style("opacity", "0.6")
          .attr("points", [
            [xx, yy],
            [xx + size, yy + size],
            [xx, yy + size + size],
            [xx - size, yy + size],
          ])
          .on("mouseover", function () {
            polygon.style("opacity", "1");
            line.style("opacity", "1");
            box.style("opacity", "1").raise();
            textLabel.style("opacity", "1").raise();
            textTime.style("opacity", "1").raise();
          })
          .on("mouseout", function () {
            polygon.style("opacity", "0.6");
            line.style("opacity", "0.6");
            textLabel.style("opacity", "0").lower();
            textTime.style("opacity", "0").lower();
            box.style("opacity", "0").lower();
          });
        textLabel
          .text(play.type)
          .attr("font-size", "14")
          .attr("fill", "#007ABA")
          .attr("text-anchor", "left")
          .attr(
            "style",
            "font-family: 'Roboto'; font-weight: 500; text-align: center; opacity: 0;"
          )
          .attr("x", scaleX(play.timeStamp) - 13)
          .attr("y", marginTop + heightBoxGraph + 15);
        textTime
          .text(printTime(play.timeStamp))
          .attr("font-size", "14")
          .attr("fill", "#94A0AA")
          .attr("text-anchor", "left")
          .attr(
            "style",
            "font-family: 'Roboto'; font-weight: 400; text-align: center; opacity: 0;"
          )
          .attr("x", scaleX(play.timeStamp) - 13)
          .attr("y", marginTop + heightBoxGraph + 30);
        const allWidth = Math.max(
          textLabel.node().getBBox().width,
          textTime.node().getBBox().width
        );
        box
          .attr("x", scaleX(play.timeStamp) - allWidth - 18)
          .attr("y", marginTop + heightBoxGraph + 2)
          .attr("width", allWidth + 10)
          .attr("height", marginBottom - 2);
      });

    /************************** CIRCLE PLUS TOOL TIP ****************************************/
    const toolTip = toolTipSvg.append("g");
    const circleFocus = toolTip
      .append("circle")
      .attr("stroke", "#FFFFFF")
      .attr("r", 6.5)
      .style("fill", "#007ABA")
      .style("opacity", 0);

    const xx = 0,
      yy = 0;
    const triangleToolTip = toolTip
      .append("polygon")
      .style("stroke", "#FFFFFF")
      .style("fill", "#FFFFFF")
      .attr(
        "points",
        `${scaleX(xx)},${scaleY(yy) + 60}, ${scaleX(xx) + 5},${
          scaleY(yy) + 60 - 5
        }, ${scaleX(xx) - 5},${scaleY(yy) + 60 - 5}`
      );
    const rectangleToolTip = toolTip
      .append("rect")
      .attr("width", "40")
      .attr("height", "55")
      .style("fill", "#FFFFFF")
      .attr("x", scaleX(xx) - 20)
      .attr("y", scaleY(yy));
    const lineToolTip = toolTip
      .append("rect")
      .attr("width", "40")
      .attr("height", "1")
      .style("fill", "#D2DCE2")
      .attr("x", scaleX(xx) - 20)
      .attr("y", scaleY(yy) + 35);
    const textToolTip = toolTip
      .append("text")
      .text("16.7")
      .attr("font-size", "16")
      .attr("fill", "#333D47")
      .attr("text-anchor", "middle")
      .attr(
        "style",
        "font-family: 'Roboto Condensed'; font-weight: 900; text-align: center"
      )
      .attr("x", scaleX(xx))
      .attr("y", scaleY(yy) + 15);
    const textMPHToolTip = toolTip
      .append("text")
      .text("MPH")
      .attr("font-size", "10")
      .attr("fill", "#57636F")
      .attr("text-anchor", "middle")
      .attr("style", "font-family: 'Roboto Condensed'; font-weight: 600")
      .attr("x", scaleX(xx))
      .attr("y", scaleY(yy) + 30);
    const textTimeToolTip = toolTip
      .append("text")
      .text("04:22:22")
      .attr("font-size", "10")
      .attr("fill", "#007ABA")
      .attr("text-anchor", "middle")
      .attr("style", "font-family: 'Roboto Condensed'; font-weight: 900")
      .attr("x", scaleX(xx))
      .attr("y", scaleY(yy) + 50);

    function mouseover() {
      circleFocus.style("opacity", 1);
    }

    function mousemove(x) {
      const p1 = { x: 0, y: 0 },
        p2 = { x: 1, y: 1 };
      let sw = true;
      orderedData.forEach((d, i) => {
        if (sw && +Object.keys(d)[0] >= x && i > 0) {
          p1.x = +Object.keys(orderedData[i - 1])[0];
          p1.y = orderedData[i - 1][Object.keys(orderedData[i - 1])[0]];
          p2.x = +Object.keys(d)[0];
          p2.y = d[Object.keys(d)[0]];
          sw = false;
        }
      });
      const y = p1.y + (x - p1.x) * ((p2.y - p1.y) / (p2.x - p1.x));
      const scaleXNumber = scaleX(x) - refData.current.diffScroll;

      if (scaleXNumber < 0 || scaleXNumber > divWidth) {
        toolTipSvg.style("opacity", 0);
      } else {
        toolTipSvg.style("opacity", 1);
      }

      circleFocus.attr("cx", scaleXNumber).attr("cy", scaleY(y));
      const yAxis = scaleY(y) - 65;
      triangleToolTip.attr(
        "points",
        `${scaleXNumber},${yAxis + 60}, ${scaleXNumber + 5},${
          yAxis + 60 - 5
        }, ${scaleXNumber - 5},${yAxis + 60 - 5}`
      );
      rectangleToolTip.attr("x", scaleXNumber - 20).attr("y", yAxis);
      lineToolTip.attr("x", scaleXNumber - 20).attr("y", yAxis + 35);
      textToolTip
        .text(fixDecimals(y, 1))
        .attr("x", scaleXNumber)
        .attr("y", yAxis + 15);
      textMPHToolTip.attr("x", scaleXNumber).attr("y", yAxis + 30);
      textTimeToolTip
        .text(printTime(x))
        .attr("x", scaleXNumber)
        .attr("y", yAxis + 50);
    }

    // ✅ Restore scroll position after rebuild
    const divScroller = document.getElementById("velocity-scroll");
    if (divScroller) {
      divScroller.scrollLeft = refData.current.diffScroll;

      // ✅ Native DOM scroll listener — reliable, works with D3 v6+
      const handleScroll = (e) => {
        refData.current.diffScroll = e.target.scrollLeft;
        if (refData.current.x !== undefined) {
          mousemove(refData.current.x);
        }
      };

      divScroller.addEventListener("scroll", handleScroll);
      // ✅ Store ref so next rebuild can remove it
      scrollListenerRef.current = handleScroll;
    }

    // ✅ Cleanup on unmount or before next rebuild
    return () => {
      const scroller = document.getElementById("velocity-scroll");
      if (scroller && scrollListenerRef.current) {
        scroller.removeEventListener("scroll", scrollListenerRef.current);
      }
    };
  }, [data, width, height, dataPlays]);

  return <div ref={d3Container} />;
};
